<script setup lang="ts">
import { computed } from 'vue';
import CheckIcon from '@/assets/icons/shared/check.svg';
import EditIcon from '@/assets/icons/shared/edit-outline.svg';

const {
  tag = 'div',
  type = 'context',
  checked,
  active,
  disabled,
  editable,
  noHover,
  reserveLeftSpace,
} = defineProps<{
  tag?: 'div' | 'li';
  type?: 'list' | 'context';
  checked?: boolean;
  active?: boolean;
  disabled?: boolean;
  editable?: boolean;
  noHover?: boolean;
  reserveLeftSpace?: boolean;
}>();

const emit = defineEmits<{
  click: [event: MouseEvent];
  edit: [event: MouseEvent];
}>();

const classList = computed(() => [
  {
    '_checked': checked,
    '_active': active,
    '_disabled': disabled,
    '_no-hover': noHover,
    '_reserve-left-space': reserveLeftSpace,
  },
  `_type-${type}`,
]);

const showEdit = computed(() => type === 'list' && editable && active);

const showCheck = computed(() => type === 'list' && checked && !showEdit.value);

function onClick(event: MouseEvent) {
  if (!disabled) {
    emit('click', event);
  }
}

function onEdit(event: MouseEvent) {
  event.preventDefault();
  event.stopPropagation();
  emit('edit', event);
}
</script>

<template>
  <component
    :is="tag"
    class="ui-dropdown-item"
    :class="classList"
    @click="onClick"
  >
    <slot />
    <button
      v-if="showEdit"
      type="button"
      class="edit"
      tabindex="-1"
      @click="onEdit"
      @mousedown.stop.prevent
    >
      <EditIcon class="edit-icon" />
    </button>
    <CheckIcon
      v-else-if="showCheck"
      v-postfix="'s'"
      class="check"
    />
  </component>
</template>

<style scoped lang="scss">
.ui-dropdown-item {
  display: flex;
  align-items: center;
  cursor: pointer;
  transition:
    color var(--transition-fast),
    background-color var(--transition-fast);
  word-break: break-word;

  :deep(svg):not(.check, .edit-icon) {
    width: 16px;
    height: 16px;
    transition: fill var(--transition-fast);
  }

  &._active:not(._disabled) {
    background-color: var(--color-gray-100);
  }

  &._checked {
    background-color: var(--color-yellow-200);
  }

  &._checked._active:not(._disabled) {
    background-color: var(--color-yellow-400);
  }

  &._type-context {
    height: 32px;
    padding: 0 8px;
    color: var(--color-gray-500);
    font-weight: var(--font-weight-bold);
    white-space: nowrap;

    :deep(svg) {
      fill: var(--color-gray-500);
    }

    &._active:not(._disabled) {
      color: var(--color-gray-600);

      :deep(svg) {
        fill: var(--color-gray-600);
      }
    }

    &._reserve-left-space {
      padding-left: 32px;
    }

    @media (hover: hover) {
      &:hover:not(._disabled) {
        color: var(--color-gray-600);

        :deep(svg) {
          fill: var(--color-gray-600);
        }
      }
    }
  }

  &._type-list {
    padding: 10px 12px;
    justify-content: space-between;
    font-size: var(--font-size-l);
    line-height: var(--line-height-l);
    color: var(--color-gray-1000);

    :deep(svg):not(.edit-icon) {
      fill: var(--color-gray-1000);
    }

    &._reserve-left-space {
      padding-left: 36px;
    }
  }

  &._disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  @media (hover: hover) {
    &:hover:not(._disabled, ._no-hover) {
      background-color: var(--color-gray-100);
    }
  }

  @media (hover: hover) {
    &._checked:hover:not(._disabled, ._no-hover) {
      background-color: var(--color-yellow-400);
    }
  }
}

.check {
  fill: var(--color-gray-1000);
  flex-shrink: 0;
  width: 16px;
  height: 16px;
}

.edit-icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  color: var(--color-gray-600);
  transition: color var(--transition-fast);
}

.edit {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;

  @media (hover: hover) {
    &:hover .edit-icon {
      color: var(--color-gray-1000);
    }
  }
}
</style>
